import React, {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useBlocker } from "react-router-dom";

type CancelFormContextType = {
  isDirty: boolean;
  setIsDirty: (v: boolean) => void;
  showPrompt: (
    onConfirm?: () => void,
    onCancel?: () => void,
    scope?: string,
  ) => void;
  hidePrompt: (scope?: string) => void;
  setPromptElement: (el: ReactNode, scope?: string) => void;
  confirmAction?: () => void;
  cancelAction?: () => void;
  allowNavigation: () => void;
  promptElements: Record<string, ReactNode>;
  setActiveScope: (scope: string) => void;
  activeScope: string | null;
};

const CancelFormContext = createContext<CancelFormContextType | undefined>(
  undefined,
);

interface CancelFormProviderProps {
  children: ReactNode;
}

export function CancelFormProvider({ children }: CancelFormProviderProps) {
  const [isDirty, setIsDirty] = useState(false);
  const [promptElements, setPromptElements] = useState<
    Record<string, ReactNode>
  >({});
  const [showScopes, setShowScopes] = useState<string[]>([]);
  const [actions, setActions] = useState<
    Record<string, { confirm?: () => void; cancel?: () => void }>
  >({});
  const [activeScope, setActiveScope] = useState<string | null>(null);

  const blocker = useBlocker(isDirty);
  const skipBlockRef = useRef(false);

  const showPrompt = useCallback(
    (onConfirm?: () => void, onCancel?: () => void, scope = "default") => {
      setActions((prev) => ({
        ...prev,
        [scope]: { confirm: onConfirm, cancel: onCancel },
      }));
      setShowScopes((prev) => (prev.includes(scope) ? prev : [...prev, scope]));
    },
    [],
  );

  const hidePrompt = useCallback((scope = "default") => {
    setShowScopes((prev) => prev.filter((s) => s !== scope));
    setActions((prev) => {
      const copy = { ...prev };
      delete copy[scope];
      return copy;
    });
  }, []);

  const setPromptElement = useCallback((el: ReactNode, scope = "default") => {
    setPromptElements((prev) => ({ ...prev, [scope]: el }));
  }, []);

  const allowNavigation = useCallback(() => {
    skipBlockRef.current = true;
    setIsDirty(false);
    queueMicrotask(() => {
      skipBlockRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (blocker.state === "blocked" && !skipBlockRef.current) {
      const scope = activeScope || "default";

      showPrompt(
        () => {
          blocker.proceed();
          hidePrompt(scope);
          setIsDirty(false);
        },
        () => {
          blocker.reset();
          hidePrompt(scope);
        },
        scope,
      );
    } else if (skipBlockRef.current && blocker.state === "blocked") {
      blocker.proceed();
    }
  }, [blocker, hidePrompt, showPrompt, activeScope]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  return createElement(
    CancelFormContext.Provider,
    {
      value: {
        isDirty,
        setIsDirty,
        showPrompt,
        hidePrompt,
        confirmAction: undefined,
        cancelAction: undefined,
        allowNavigation,
        setPromptElement,
        promptElements,
        setActiveScope,
        activeScope,
      },
    },
    children,
    Object.entries(promptElements).map(([scope, element]) =>
      showScopes.includes(scope)
        ? createElement(
            React.Fragment,
            { key: scope },
            React.cloneElement(element as any, {
              onConfirm: actions[scope]?.confirm,
              onCancel: actions[scope]?.cancel,
            }),
          )
        : null,
    ),
  );
}

export function useCancelForm(): CancelFormContextType {
  const ctx = useContext(CancelFormContext);
  if (!ctx) {
    throw new Error("useCancelForm must be used within CancelFormProvider");
  }
  return ctx;
}
